import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import {
  MessageCircle, Loader2, RefreshCw, Send, Clock, Bot, Zap,
  ShieldCheck, AlertTriangle, CheckCircle2, Sparkles, Lock, Check, CheckCheck, BookOpen, Lightbulb,
  DollarSign, RotateCcw, Search, CalendarDays
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatLocal } from '../lib/dateUtils';

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function DatePickerButton({ value, onChange, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs font-normal justify-start w-[140px]" data-testid={testid}>
          <CalendarDays className="h-3.5 w-3.5 mr-1.5 opacity-70" />
          {value ? value : <span className="text-muted-foreground">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ? new Date(`${value}T00:00:00`) : undefined}
          onSelect={(d) => { if (d) onChange(toISO(d)); setOpen(false); }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${active ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-600' : 'border-border text-muted-foreground hover:text-foreground'}`}
    >
      {children}
    </button>
  );
}

function windowLabel(w) {
  if (!w) return null;
  if (w.open) {
    const hrs = Math.floor((w.seconds_remaining || 0) / 3600);
    const mins = Math.floor(((w.seconds_remaining || 0) % 3600) / 60);
    return { open: true, text: hrs >= 1 ? `Window open · ${hrs}h ${mins}m left` : `Window open · ${mins}m left` };
  }
  return { open: false, text: w.reason === 'no_inbound' ? 'No inbound yet' : '24h window closed' };
}

// ─── Conversation list row ───────────────────────────────
function ConvRow({ conv, active, onClick }) {
  const wl = windowLabel(conv.window);
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-3 border-b border-border/40 transition-colors hover:bg-muted/40 ${active ? 'bg-muted/60' : ''}`}
      data-testid={`wa-conv-${conv.lead_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm truncate flex items-center gap-1.5">
          {conv.human_needed && (
            <span className="h-2 w-2 rounded-full bg-red-500 shrink-0 animate-pulse" data-testid={`wa-conv-human-needed-dot-${conv.lead_id}`} title="AI paused — human needed" />
          )}
          {conv.lead_name}
        </span>
        <span className="text-[10px] text-muted-foreground shrink-0">{conv.last_ts ? formatLocal(conv.last_ts) : ''}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        {conv.last_direction === 'inbound' && <Bot className="h-3 w-3 text-sky-500 shrink-0" />}
        <p className="text-xs text-muted-foreground truncate flex-1">{conv.last_message || '—'}</p>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        {conv.human_needed && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-red-500/50 text-red-400 bg-red-500/10" data-testid={`wa-conv-human-needed-${conv.lead_id}`}>
            <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />AI paused
          </Badge>
        )}
        {conv.awaiting_reply && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/40 text-amber-500">Needs reply</Badge>
        )}
        {conv.has_pending_draft && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-violet-500/40 text-violet-400">
            <Sparkles className="h-2.5 w-2.5 mr-0.5" />Draft ready
          </Badge>
        )}
        {wl && (
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${wl.open ? 'border-emerald-500/40 text-emerald-500' : 'border-zinc-600 text-zinc-400'}`}>
            <Clock className="h-2.5 w-2.5 mr-0.5" />{wl.open ? 'Open' : 'Closed'}
          </Badge>
        )}
      </div>
    </button>
  );
}

// ─── Delivery status tick (sent → delivered → read) ──────
function friendlyDeliveryError(code, reason) {
  if (code === 131049 || code === '131049')
    return "Not delivered — Meta withheld this marketing message (131049). Send a UTILITY template or wait for the lead to reply.";
  if (code === 131047 || code === '131047')
    return "Not delivered — 24h window closed (131047). Send an approved template to re-open it.";
  if (code === 131026 || code === '131026')
    return "Not delivered — recipient number is not on WhatsApp (131026).";
  if (code === 470 || code === '470')
    return "Not delivered — outside the 24h customer-care window (470).";
  return reason ? `Not delivered — ${reason}${code ? ` (${code})` : ''}` : 'Message failed to deliver';
}

function DeliveryTick({ status, code, reason }) {
  if (status === 'read') return <CheckCheck className="h-3 w-3 text-sky-300" title="Read" />;
  if (status === 'delivered') return <CheckCheck className="h-3 w-3 opacity-70" title="Delivered" />;
  if (status === 'failed') return <AlertTriangle className="h-3 w-3 text-red-300" title={friendlyDeliveryError(code, reason)} />;
  return <Check className="h-3 w-3 opacity-60" title="Sent (accepted by Meta — awaiting delivery)" />;
}

// ─── Message bubble ──────────────────────────────────────
function Bubble({ msg }) {
  const out = msg.direction === 'outbound';
  const isDraft = msg.status === 'draft_pending';
  return (
    <div className={`flex ${out ? 'justify-end' : 'justify-start'} mb-2`} data-testid={`wa-msg-${msg.id}`}>
      <div className={`max-w-[75%] rounded-xl px-3.5 py-2 shadow-sm ${out ? (isDraft ? 'bg-violet-900/40 border border-violet-500/40 text-violet-100 rounded-tr-sm' : 'bg-[#005c4b] text-white rounded-tr-sm') : 'bg-[#1f2c33] text-zinc-100 border border-zinc-700/50 rounded-tl-sm'}`}>
        {isDraft && <div className="flex items-center gap-1 text-[10px] text-violet-300 mb-1"><Sparkles className="h-2.5 w-2.5" />Brain draft — awaiting approval</div>}
        {isDraft && msg.answer_source && (
          <div className="mb-1" data-testid={`wa-answer-source-${msg.id}`}>
            {msg.answer_source === 'knowledge_base' ? (
              <span className="inline-flex items-center gap-1 text-[9.5px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-200 border border-blue-400/30" title="Grounded in your approved Knowledge Base">
                <BookOpen className="h-2.5 w-2.5" />From Knowledge Base
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[9.5px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-400/30" title="Your KB didn't cover this — answered from general expertise. Review before sending.">
                <Lightbulb className="h-2.5 w-2.5" />General expertise
              </span>
            )}
          </div>
        )}
        <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{msg.message}</p>
        {out && !isDraft && msg.delivery_status === 'failed' && (
          <div className="mt-1 flex items-start gap-1 text-[10px] text-red-300 bg-red-500/10 border border-red-400/25 rounded px-1.5 py-1"
               data-testid={`wa-delivery-error-${msg.id}`}>
            <AlertTriangle className="h-2.5 w-2.5 mt-0.5 flex-shrink-0" />
            <span>{friendlyDeliveryError(msg.delivery_error_code, msg.delivery_error_reason)}</span>
          </div>
        )}
        <div className="flex items-center justify-end gap-1.5 mt-1">
          {msg.auto_triggered && out && <Zap className="h-3 w-3 text-amber-300" />}
          <span className="text-[10px] opacity-60">{msg.timestamp ? formatLocal(msg.timestamp) : ''}</span>
          {out && !isDraft && <DeliveryTick status={msg.delivery_status} code={msg.delivery_error_code} reason={msg.delivery_error_reason} />}
        </div>
      </div>
    </div>
  );
}

// ─── Main hub ────────────────────────────────────────────
export default function WhatsAppConversations({ embedded = false }) {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [autonomy, setAutonomy] = useState('auto');
  const [pricingPolicy, setPricingPolicy] = useState('escalate');
  const [resuming, setResuming] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [search, setSearch] = useState('');
  const scrollRef = useRef(null);

  const pausedCount = convs.filter((c) => c.human_needed).length;

  const matchDate = (ts) => {
    if (dateFilter === 'all' || !ts) return true;
    const d = new Date(ts).getTime();
    if (Number.isNaN(d)) return true;
    if (dateFilter === 'custom') {
      if (customFrom) { const f = new Date(customFrom); f.setHours(0, 0, 0, 0); if (d < f.getTime()) return false; }
      if (customTo) { const t = new Date(customTo); t.setHours(23, 59, 59, 999); if (d > t.getTime()) return false; }
      return true;
    }
    if (dateFilter === 'today') { const t = new Date(); t.setHours(0, 0, 0, 0); return d >= t.getTime(); }
    const days = dateFilter === '7d' ? 7 : 30;
    return d >= Date.now() - days * 86400000;
  };
  const matchStatus = (c) => {
    if (statusFilter === 'window_open') return !!(c.window && c.window.open);
    if (statusFilter === 'unread') return c.last_direction === 'inbound';
    if (statusFilter === 'needs_reply') return !!c.awaiting_reply;
    if (statusFilter === 'human_needed') return !!c.human_needed;
    if (statusFilter === 'draft') return !!c.has_pending_draft;
    return true;
  };
  const q = search.trim().toLowerCase();
  const filteredConvs = convs.filter((c) => matchStatus(c) && matchDate(c.last_ts)
    && (!q || `${c.lead_name || ''} ${c.phone || ''}`.toLowerCase().includes(q)));

  const loadConvs = useCallback(async () => {
    try {
      const [cRes, aRes] = await Promise.all([
        api.get('/api/whatsapp/conversations'),
        api.get('/api/whatsapp/autonomy'),
      ]);
      setConvs(cRes.data?.conversations || []);
      setAutonomy(aRes.data?.autonomy_mode || 'auto');
      setPricingPolicy(aRes.data?.pricing_reply_policy || 'escalate');
    } catch (e) {
      console.error('Failed to load conversations', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const composerRef = useRef('');
  useEffect(() => { composerRef.current = composer; }, [composer]);

  const loadThread = useCallback(async (leadId, { silent = false } = {}) => {
    if (!leadId) return;
    if (!silent) setThreadLoading(true);
    try {
      const res = await api.get(`/api/whatsapp/conversation/${leadId}`);
      setThread(res.data);
      const draft = res.data?.pending_draft?.message || '';
      // Never clobber text the user is typing during a background poll.
      if (!silent) setComposer(draft);
      else if (draft && !composerRef.current) setComposer(draft);
    } catch {
      if (!silent) setThread(null);
    } finally {
      if (!silent) setThreadLoading(false);
    }
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  // Deep-link: /whatsapp-inbox?lead=<id> auto-selects that conversation.
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    const leadParam = params.get('lead');
    if (leadParam && !selected) {
      const match = convs.find((c) => c.lead_id === leadParam);
      setSelected(match || { lead_id: leadParam, lead_name: 'Lead', lead_company: '', phone: null });
    }
  }, [loading, convs, selected]);
  const selectedId = selected?.lead_id;
  useEffect(() => { if (selectedId) loadThread(selectedId); }, [selectedId, loadThread]);
  // Keep the open conversation's header/badges in sync with the refreshed list.
  useEffect(() => {
    if (!selected) return;
    const fresh = convs.find((c) => c.lead_id === selected.lead_id);
    if (fresh && JSON.stringify(fresh) !== JSON.stringify(selected)) setSelected(fresh);
  }, [convs, selected]);

  // Refresh = list + open thread (inbound replies land in the thread, not the list).
  const refreshAll = useCallback(async () => {
    await Promise.all([loadConvs(), selectedId ? loadThread(selectedId) : Promise.resolve()]);
  }, [loadConvs, loadThread, selectedId]);

  // Live updates: poll the open thread every 10s and the list every 30s while the tab is visible.
  useEffect(() => {
    const tick = (fn) => () => { if (document.visibilityState === 'visible') fn(); };
    const threadTimer = selectedId ? setInterval(tick(() => loadThread(selectedId, { silent: true })), 10000) : null;
    const listTimer = setInterval(tick(loadConvs), 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') { loadConvs(); if (selectedId) loadThread(selectedId, { silent: true }); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (threadTimer) clearInterval(threadTimer);
      clearInterval(listTimer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [selectedId, loadConvs, loadThread]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const toggleAutonomy = async (mode) => {
    setAutonomy(mode);
    try {
      await api.put('/api/whatsapp/autonomy', { autonomy_mode: mode });
      toast.success(mode === 'auto' ? 'Brain will auto-reply within the 24h window' : 'Brain will draft replies for your approval');
    } catch {
      toast.error('Could not update autonomy');
    }
  };

  const togglePricing = async () => {
    const next = pricingPolicy === 'auto_answer' ? 'escalate' : 'auto_answer';
    setPricingPolicy(next);
    try {
      await api.put('/api/whatsapp/autonomy', { pricing_reply_policy: next });
      toast.success(next === 'auto_answer'
        ? 'AI will now answer pricing questions from your knowledge base'
        : 'Pricing questions will be handed to a human');
    } catch {
      setPricingPolicy(pricingPolicy);
      toast.error('Could not update pricing policy');
    }
  };

  const resumeAll = async () => {
    setResuming(true);
    try {
      const res = await api.post('/api/whatsapp/conversations/resume-all');
      toast.success(res.data?.message || 'AI resumed on paused conversations');
      await loadConvs();
    } catch {
      toast.error('Could not resume paused conversations');
    } finally {
      setResuming(false);
    }
  };

  const windowOpen = thread?.window?.open;
  const pendingDraft = thread?.pending_draft;

  const handleSend = async () => {
    if (!composer.trim() || !selected) return;
    setSending(true);
    try {
      if (pendingDraft) {
        await api.post(`/api/whatsapp/conversations/${selected.lead_id}/approve-draft`, { message: composer });
        toast.success('Draft approved and sent');
      } else {
        await api.post(`/api/whatsapp/conversations/${selected.lead_id}/send`, { message: composer });
        toast.success('Message sent');
      }
      setComposer('');
      await loadThread(selected.lead_id);
      loadConvs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="whatsapp-conversations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        {!embedded ? (
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <MessageCircle className="h-6 w-6 text-emerald-500" />WhatsApp Inbox
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Two-way conversations with window-aware replies & Brain autonomy</p>
          </div>
        ) : <div />}
        <div className="flex items-center gap-3">
          {pausedCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={resumeAll}
              disabled={resuming}
              className="border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
              data-testid="wa-resume-all-btn"
            >
              {resuming ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
              Resume all paused ({pausedCount})
            </Button>
          )}
          {/* Pricing policy toggle */}
          <button
            onClick={togglePricing}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${pricingPolicy === 'auto_answer' ? 'border-emerald-600 bg-emerald-600/10 text-emerald-600' : 'border-border text-muted-foreground hover:text-foreground'}`}
            data-testid="wa-pricing-toggle"
            title="When ON, the AI answers pricing questions from your knowledge base instead of handing them to a human"
          >
            <DollarSign className="h-3.5 w-3.5" />
            {pricingPolicy === 'auto_answer' ? 'AI answers pricing' : 'Pricing → human'}
          </button>
          {/* Autonomy toggle */}
          <div className="flex items-center rounded-lg border border-border p-0.5" data-testid="wa-autonomy-toggle">
            <button
              onClick={() => toggleAutonomy('auto')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${autonomy === 'auto' ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="wa-autonomy-auto"
            >
              <Zap className="h-3.5 w-3.5" />Auto-reply
            </button>
            <button
              onClick={() => toggleAutonomy('draft')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${autonomy === 'draft' ? 'bg-violet-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="wa-autonomy-draft"
            >
              <ShieldCheck className="h-3.5 w-3.5" />Draft & approve
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={refreshAll} data-testid="wa-refresh-convs">
            <RefreshCw className="h-4 w-4 mr-1" />Refresh
          </Button>
        </div>
      </div>

      {/* Filter toolbar — kept above the inbox for a clean, uncluttered list */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card/40 px-3 py-2" data-testid="wa-filter-bar">
        <div className="relative w-full sm:w-64">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or phone"
            className="h-8 text-xs pl-8"
            data-testid="wa-filter-search"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1" data-testid="wa-filter-status">
          {[
            { k: 'all', label: 'All', n: convs.length },
            { k: 'window_open', label: 'Window open', n: convs.filter((c) => c.window && c.window.open).length },
            { k: 'unread', label: 'Unread', n: convs.filter((c) => c.last_direction === 'inbound').length },
            { k: 'needs_reply', label: 'Needs reply', n: convs.filter((c) => c.awaiting_reply).length },
            { k: 'human_needed', label: 'AI paused', n: pausedCount },
            { k: 'draft', label: 'Draft ready', n: convs.filter((c) => c.has_pending_draft).length },
          ].map((t) => (
            <FilterPill key={t.k} active={statusFilter === t.k} onClick={() => setStatusFilter(t.k)} testid={`wa-filter-${t.k}`}>
              {t.label}{t.n ? ` ${t.n}` : ''}
            </FilterPill>
          ))}
        </div>
        <div className="sm:ml-auto flex flex-wrap items-center gap-1" data-testid="wa-filter-date">
          {[{ k: 'all', label: 'All' }, { k: 'today', label: 'Today' }, { k: '7d', label: '7d' }, { k: '30d', label: '30d' }, { k: 'custom', label: 'Custom' }].map((t) => (
            <FilterPill key={t.k} active={dateFilter === t.k} onClick={() => setDateFilter(t.k)} testid={`wa-date-${t.k}`}>{t.label}</FilterPill>
          ))}
          {dateFilter === 'custom' && (
            <div className="flex items-center gap-1" data-testid="wa-custom-date">
              <DatePickerButton value={customFrom} onChange={setCustomFrom} placeholder="From" testid="wa-custom-from" />
              <span className="text-xs text-muted-foreground">to</span>
              <DatePickerButton value={customTo} onChange={setCustomTo} placeholder="To" testid="wa-custom-to" />
            </div>
          )}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] h-[600px]">
          {/* Left: conversation list */}
          <div className="border-r border-border overflow-y-auto min-h-0" data-testid="wa-conv-list">
            {loading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : convs.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-30" />
                No conversations yet
              </div>
            ) : filteredConvs.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground" data-testid="wa-no-match">
                No conversations match these filters.
              </div>
            ) : (
              filteredConvs.map((c) => (
                <ConvRow key={c.lead_id} conv={c} active={selected?.lead_id === c.lead_id} onClick={() => setSelected(c)} />
              ))
            )}
          </div>

          {/* Right: thread + composer */}
          <div className="flex flex-col min-h-0 overflow-hidden">
            {!selected ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <MessageCircle className="h-10 w-10 opacity-30 mb-2" />
                <p className="text-sm">Select a conversation</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
                  <div>
                    <p className="text-sm font-medium" data-testid="wa-thread-name">{selected.lead_name}</p>
                    <p className="text-[11px] text-muted-foreground">{selected.lead_company || selected.phone || ''}</p>
                  </div>
                  {thread?.window && (
                    <Badge variant="outline" className={`text-[11px] ${windowOpen ? 'border-emerald-500/40 text-emerald-500' : 'border-zinc-600 text-zinc-400'}`} data-testid="wa-window-badge">
                      <Clock className="h-3 w-3 mr-1" />{windowLabel(thread.window)?.text}
                    </Badge>
                  )}
                </div>

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 bg-[#0b141a]/40" data-testid="wa-thread-body">
                  {threadLoading ? (
                    <div className="flex items-center justify-center h-full"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                  ) : (thread?.thread || []).length === 0 ? (
                    <div className="flex items-center justify-center h-full text-sm text-muted-foreground">No messages yet</div>
                  ) : (
                    thread.thread.map((m) => <Bubble key={m.id} msg={m} />)
                  )}
                </div>

                {/* Composer */}
                <div className="border-t border-border p-3">
                  {pendingDraft && (
                    <div className="flex items-center gap-1.5 text-xs text-violet-400 mb-2" data-testid="wa-draft-banner">
                      <Sparkles className="h-3.5 w-3.5" />Brain drafted this reply — edit if needed, then approve & send.
                    </div>
                  )}
                  {windowOpen ? (
                    <div className="flex items-end gap-2">
                      <Textarea
                        value={composer}
                        onChange={(e) => setComposer(e.target.value)}
                        placeholder="Type a message…"
                        rows={2}
                        className="text-sm resize-none"
                        data-testid="wa-composer-input"
                      />
                      <Button
                        onClick={handleSend}
                        disabled={sending || !composer.trim()}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="wa-composer-send"
                      >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : pendingDraft ? <><CheckCircle2 className="h-4 w-4 mr-1" />Approve</> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2.5" data-testid="wa-window-closed-note">
                      <Lock className="h-4 w-4 text-amber-500 shrink-0" />
                      <p className="text-xs text-amber-300 flex-1">
                        The 24h customer-care window is closed. Send a pre-approved <span className="font-medium">template</span> from the lead's WhatsApp tab to re-open it.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
